import React from 'react';
import { PremiumInfoCircleIcon } from './PremiumHeatmapIcons';

export interface LiveEventAreaItem {
    id: string;
    name: string;
    attendees: number;
    percentage_of_total: number;
    most_popular_event: string;
    demographics: string;
    /** Used to highlight selected area (e.g. match map selection) */
    isSelected?: boolean;
}

interface LiveEventActivityLevelsPanelProps {
    items: LiveEventAreaItem[];
    /** First item uses primary color for count (e.g. #004232) */
    showPrimaryHighlight?: boolean;
}

export const LiveEventActivityLevelsPanel: React.FC<LiveEventActivityLevelsPanelProps> = ({
    items,
    showPrimaryHighlight = true,
}) => {
    return (
        <div className="w-full xl:w-[304px] xl:h-[554px] bg-white dark:bg-gray-800 overflow-hidden rounded-[16px] outline outline-1 outline-[#E5E7EB] dark:outline-gray-700 outline-offset-[-1px] flex flex-col min-h-0 p-[0.80px]">
            <div className="p-[20px] flex items-center justify-between shrink-0">
                <div className="text-[#111827] dark:text-white text-[15px] font-normal leading-[28px]" style={{ fontFamily: 'Nunito' }}>
                    Activity Levels
                </div>
                <div className="w-[20px] h-[16px] flex items-center justify-center">
                    <PremiumInfoCircleIcon size={16} className="text-[#4B5563] dark:text-gray-300" />
                </div>
            </div>

            <div className="px-[15px] pb-[15px] flex-1 min-h-0 overflow-y-auto flex flex-col gap-[16px]">
                {items.map((item, idx) => (
                    <div
                        key={item.id}
                        className="p-[19.8px] bg-white dark:bg-gray-800 rounded-[16px] outline outline-1 outline-[#E5E7EB] dark:outline-gray-700 outline-offset-[-1px]"
                        style={{ boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)' }}
                    >
                        <div className="flex items-center justify-between gap-3">
                            <div className="text-[#111827] dark:text-white text-[18px] font-normal leading-[28px]" style={{ fontFamily: 'Nunito' }}>
                                {item.name}
                            </div>
                            <div className="w-[31px] h-[31px] bg-[#5EC269] rounded-full flex items-center justify-center shrink-0">
                                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                    <path d="M2 5.2L4.1 7.3L8 3.4" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                        </div>

                        <div className="mt-[12px] flex flex-col gap-[4px]">
                            <div
                                className="text-[23px] font-normal leading-[28px]"
                                style={{
                                    fontFamily: 'Nunito',
                                    color: showPrimaryHighlight && idx === 0 ? 'var(--button-text, #004232)' : '#5EC269',
                                }}
                            >
                                {item.attendees}
                            </div>
                            <div className="text-[#364153] dark:text-gray-300 text-[14px] font-normal leading-[20px]" style={{ fontFamily: 'Nunito' }}>
                                {item.percentage_of_total.toFixed(1)}% of total attendees
                            </div>
                        </div>

                        <div className="mt-[20px] pt-[20px] flex flex-col gap-[12px]" style={{ borderTop: '0.8px solid #E5E7EB' }}>
                            <div className="flex items-start gap-[8px]">
                                <div className="w-[16px] h-[18px] pt-[2px] shrink-0">
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <circle cx="8" cy="8" r="6.67" stroke="#00C49A" strokeWidth="1.2" />
                                        <path d="M8 4v4l2.67 2.67" stroke="#00C49A" strokeWidth="1.2" strokeLinecap="round" />
                                    </svg>
                                </div>
                                <div className="flex flex-col gap-[2px]">
                                    <div className="text-[#6B7280] dark:text-gray-400 text-[11px] font-normal leading-[14px]" style={{ fontFamily: 'Nunito' }}>
                                        Most Popular Event
                                    </div>
                                    <div className="text-[#111827] dark:text-white text-[14px] font-normal leading-[18px]" style={{ fontFamily: 'Nunito' }}>
                                        {item.most_popular_event}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-start gap-[8px]">
                                <div className="w-[16px] h-[18px] pt-[2px] shrink-0">
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M2.67 10.67h10.66" stroke="#4E80EE" strokeWidth="1.2" strokeLinecap="round" />
                                        <circle cx="8" cy="4" r="2.67" stroke="#4E80EE" strokeWidth="1.2" />
                                    </svg>
                                </div>
                                <div className="flex flex-col gap-[2px]">
                                    <div className="text-[#6B7280] dark:text-gray-400 text-[11px] font-normal leading-[14px]" style={{ fontFamily: 'Nunito' }}>
                                        Demographics
                                    </div>
                                    <div className="text-[#4B5563] dark:text-gray-300 text-[12px] font-normal leading-[16px]" style={{ fontFamily: 'Nunito' }}>
                                        {item.demographics}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
